import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { getDb } from '@/db/viewerDb'
import { clearClientState } from '@/utils/clearClientState'

interface CacheStats {
  contentBundles: number
  mediaAssets: number
  notes: number
  bookmarks: number
  customViews: number
  pendingNoteChanges: number
  pendingBookmarkChanges: number
}

async function loadCacheStats(): Promise<CacheStats> {
  const db = await getDb()
  const [contentBundles, mediaAssets, notes, bookmarks, customViews, pendingNoteChanges, pendingBookmarkChanges] =
    await Promise.all([
      db.count('content-bundle'),
      db.count('media-cache'),
      db.count('notes'),
      db.count('bookmarks'),
      db.count('custom-views'),
      db.count('offline-queue'),
      db.count('bookmark-offline-queue'),
    ])
  return { contentBundles, mediaAssets, notes, bookmarks, customViews, pendingNoteChanges, pendingBookmarkChanges }
}

export function SettingsManager() {
  const { t } = useTranslation()
  const [stats, setStats] = useState<CacheStats | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)
  const [clearing, setClearing] = useState(false)

  useEffect(() => {
    loadCacheStats().then(setStats)
  }, [])

  const hasPendingChanges = stats !== null && (stats.pendingNoteChanges > 0 || stats.pendingBookmarkChanges > 0)

  const handleConfirmClear = async () => {
    setClearing(true)
    try {
      await clearClientState()
    } finally {
      window.location.replace('/')
    }
  }

  return (
    <Box sx={{ pt: 2 }}>
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t('settings.cachedData.title')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {stats === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {t('settings.cachedData.loading')}
              </Typography>
            </Box>
          ) : (
            <>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText primary={t('settings.cachedData.contentBundles', { count: stats.contentBundles })} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('settings.cachedData.mediaAssets', { count: stats.mediaAssets })} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('settings.cachedData.notes', { count: stats.notes })} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('settings.cachedData.bookmarks', { count: stats.bookmarks })} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('settings.cachedData.customViews', { count: stats.customViews })} />
                </ListItem>
              </List>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('settings.cachedData.outOfSyncTitle')}
                </Typography>
                {!hasPendingChanges ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.cachedData.noOutOfSync')}
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {stats.pendingNoteChanges > 0 && (
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('settings.cachedData.pendingNoteChanges', { count: stats.pendingNoteChanges })}
                          primaryTypographyProps={{ color: 'warning.main' }}
                        />
                      </ListItem>
                    )}
                    {stats.pendingBookmarkChanges > 0 && (
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('settings.cachedData.pendingBookmarkChanges', { count: stats.pendingBookmarkChanges })}
                          primaryTypographyProps={{ color: 'warning.main' }}
                        />
                      </ListItem>
                    )}
                  </List>
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('settings.cachedData.clearHint')}
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => setConfirmClear(true)}
                >
                  {t('settings.cachedData.clearButton')}
                </Button>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={confirmClear} onClose={() => !clearing && setConfirmClear(false)}>
        <DialogTitle>{t('settings.cachedData.clearConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('settings.cachedData.clearConfirmMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)} disabled={clearing}>
            {t('settings.cachedData.clearCancel')}
          </Button>
          <Button
            onClick={handleConfirmClear}
            color="error"
            variant="contained"
            disabled={clearing}
            startIcon={clearing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {clearing ? t('settings.cachedData.clearing') : t('settings.cachedData.clearConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
